import React, { lazy } from 'react';

import AnalyticsIcon from '@mui/icons-material/AnalyticsOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import PageviewIcon from '@mui/icons-material/PageviewOutlined';
import PreviewIcon from '@mui/icons-material/PreviewOutlined';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import SummarizeIcon from '@mui/icons-material/SummarizeOutlined';

import { CONFIGURE_SETTINGS, IMPORT_TESTS } from '@rights';
import Breadcrumb from '@common/components/breadcrumb/breadcrumb';

import V2SettingsIframe from '@aware/common/v2-settings-iframe';
import { V2_ROUTE_PAGES } from '@aware/v2-routes/constants';
import AxiomClassic from '@aware/pages/v2-axiom/axiom-classic';
import V2ReportsPage from '@aware/common/v2-reports-page/v2-reports-page';

const V2Reports = lazy(
  () => import(/* webpackChunkName: "aware-analysis-reports" */ '@aware/pages/v2-reports')
);

const Axiom = lazy(
  () => import(/* webpackChunkName: "aware-analysis-axiom" */ '@aware/pages/v2-axiom')
);

const V2AnalysisReportsPage = ({ match: { params } }) =>
  /^(student-constructed-response-report|l4w-quintile)$/i.test(params.reportId) ? (
    <V2Reports id={params.id} />
  ) : (
    <V2ReportsPage tabId="Analyze" baseRoute={V2_ROUTE_PAGES.analysisReports} />
  );

const ANALYSIS_ROUTE_CONFIG = {
  title: 'Analysis',
  settings: [
    {
      title: 'Test Settings',
      order: 1,
      paths: [
        {
          path: `${V2_ROUTE_PAGES.analysisSettings}/upload-test-files`,
          title: 'Upload Test Files',
          exact: true,
          rights: [{ right: [IMPORT_TESTS] }],
          component: () => (
            <V2SettingsIframe
              iframeSrc="/aware/?embed=1&detailsSrc=/aware/manage/import/testimport.aspx"
              title="Upload Test Files"
            />
          )
        },
        {
          path: `${V2_ROUTE_PAGES.analysisSettings}/configuration-options`,
          title: 'Configuration Options',
          exact: true,
          rights: [{ right: [CONFIGURE_SETTINGS] }],
          component: () => (
            <V2SettingsIframe
              iframeSrc="/aware/?embed=1&detailsSrc=/aware/manage/optionspage.aspx"
              title="Configuration Options"
            />
          )
        }
      ]
    }
  ],

  quickViews: {
    path: V2_ROUTE_PAGES.analysisQuickViews,
    icon: <PreviewIcon />,
    title: 'Quick Views',
    breadcrumb: () => (
      <Breadcrumb title="Quick Views" to={V2_ROUTE_PAGES.analysisQuickViews} />
    ),
    order: 2,
    routes: [
      {
        path: `${V2_ROUTE_PAGES.analysisQuickViews}`,
        component: lazy(
          () =>
            import(
              /* webpackChunkName: "aware-analysis-quick-views" */ '@aware/pages/v2-analysis-quick-views/quick-views'
            )
        )
      }
    ]
  },
  dataViewsBank: {
    path: V2_ROUTE_PAGES.analysisDataViewsBank, // Navigation Link
    icon: <PageviewIcon />,
    title: 'Data Views Bank',
    breadcrumb: () => (
      <Breadcrumb title="Data Views Bank" to={V2_ROUTE_PAGES.analysisDataViewsBank} />
    ),
    order: 3,
    routes: [
      {
        path: `${V2_ROUTE_PAGES.analysisDataViewsBank}`,
        component: lazy(
          () =>
            import(
              /* webpackChunkName: "aware-analysis-data-views-bank" */ '@aware/pages/v2-analysis-data-views-bank/data-views-bank'
            )
        )
      }
    ]
  },
  single: {
    path: V2_ROUTE_PAGES.analysisSingleTest,
    icon: <AnalyticsIcon />,
    title: 'Single Test',
    breadcrumb: () => (
      <Breadcrumb title="Single Test" to={V2_ROUTE_PAGES.analysisSingleTest} />
    ),
    order: 4,
    exact: true,
    routes: [
      {
        path: `${V2_ROUTE_PAGES.analysisSingleTest}/:id?/:view?`, // Render component
        breadcrumb: null,
        component: lazy(
          () =>
            import(
              /* webpackChunkName: "aware-analysis-single" */ '@aware/pages/v2-analysis-single-test/single-test-analysis'
            )
        )
      }
    ]
  },
  multi: {
    path: V2_ROUTE_PAGES.analysisMultiTest,
    icon: <StackedLineChartIcon />,
    title: 'Multi-Test',
    breadcrumb: () => (
      <Breadcrumb title="Multi-Test" to={V2_ROUTE_PAGES.analysisMultiTest} />
    ),
    order: 5,
    exact: true,
    routes: [
      {
        path: `${V2_ROUTE_PAGES.analysisMultiTest}/:id?/:view?`, // Render component
        breadcrumb: null,
        component: lazy(
          () =>
            import(
              /* webpackChunkName: "aware-analysis-multi" */ '@aware/pages/v2-analysis-multi-test/multi-test-analysis'
            )
        )
      }
    ]
  },

  reports: {
    path: V2_ROUTE_PAGES.analysisReports,
    icon: <SummarizeIcon />,
    title: 'Reports',
    collapsedTitle: 'Analysis Reports',
    order: 6,
    exact: true,
    breadcrumb: () => <Breadcrumb title="Reports" to={V2_ROUTE_PAGES.analysisReports} />,
    component: V2AnalysisReportsPage,
    routes: [
      {
        path: `${V2_ROUTE_PAGES.analysisReports}/:reportId/:id?`,
        breadcrumb: null,
        component: V2AnalysisReportsPage
      }
    ]
  },

  axiom: {
    path: V2_ROUTE_PAGES.analysisAxiom,
    icon: <InsightsIcon />,
    title: 'Axiom',
    order: 7,
    component: AxiomClassic
  },

  axiom3: {
    path: V2_ROUTE_PAGES.analysisAxiom3,
    icon: <InsightsIcon />,
    title: 'Axiom',
    order: 8,
    component: Axiom,
    breadcrumb: () => <Breadcrumb title="Axiom" to={V2_ROUTE_PAGES.analysisAxiom3} />
  }
};

export default ANALYSIS_ROUTE_CONFIG;
